import React, { useState, useEffect } from 'react';
import { 
  ComposedChart, 
  CartesianGrid,
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  ReferenceLine,
  Bar
} from 'recharts';
import { Button, Radio, Row, Col, Card, Typography, Divider, Alert, Space, message } from 'antd';
import { Candle, PatternPoint } from '../types';
import { calculatePredictionLines } from '../utils/patternAnalysis';
import moment from 'moment';
import './CandleStickChart.css';
import TrendSummary from './TrendSummary';
import { useI18n } from '../i18n/I18nProvider';
import type { TranslationKey } from '../i18n/translations';

const { Title } = Typography;

interface CandleStickChartProps {
  candles: Candle[];
  patterns: PatternPoint[];
}

// 为图表准备的数据格式
interface ChartData {
  time: string;
  open: number;
  close: number;
  high: number;
  low: number;
  volume: number;
  color?: string;
  isRise?: boolean;
  value: number; // 保存实际价格，用于计算
  [key: string]: string | number | boolean | undefined; // 用于存储预测线的动态属性
}

const CandleStickChart: React.FC<CandleStickChartProps> = ({ candles }) => {
  const { t } = useI18n();
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [selectedType, setSelectedType] = useState<'top' | 'bottom'>('bottom');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [manualPattern, setManualPattern] = useState<PatternPoint | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [loading, setLoading] = useState(false);
  const [yDomain, setYDomain] = useState<[number, number] | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [referencePointA, setReferencePointA] = useState<string>('');
  const [referencePointB, setReferencePointB] = useState<string>('');
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 768px)')?.matches ?? false;
  });

  const modeLabel = selectedType === 'top' ? t('chart.mode.top') : t('chart.mode.bottom');

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    setIsMobile(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  
  // 准备图表数据
  useEffect(() => {
    if (!candles.length) return;
    
    // 将数据转换为图表需要的格式
    const data: ChartData[] = candles.map((candle, index) => {
      const isRise = candle.close >= candle.open;
      return {
      time: moment(candle.timestamp).format('MM-DD HH:mm'),
      open: candle.open,
      close: candle.close,
      high: candle.high,
      low: candle.low,
      volume: candle.volume,
        color: isRise ? '#26a69a' : '#ef5350',
        isRise,
        value: candle.close // 使用收盘价作为主要显示值
      };
    });
    
    // 计算Y轴的范围，使K线在中间显示
    const highValues = data.map(d => d.high);
    const lowValues = data.map(d => d.low);
    const highestValue = Math.max(...highValues);
    const lowestValue = Math.min(...lowValues);
    
    // 为了美观，上下各留出10%的空间
    const valueRange = highestValue - lowestValue;
    const paddingTop = valueRange * 0.1;
    const paddingBottom = valueRange * 0.1;
    
    const yMax = highestValue + paddingTop;
    const yMin = lowestValue - paddingBottom;
    
    // 设置Y轴的范围
    setYDomain([yMin, yMax]);
    
    // console.log("K线数据范围:", {
    //   最高价: highestValue,
    //   最低价: lowestValue,
    //   Y轴范围: [yMin, yMax],
    //   数据点数: data.length
    // });
    
    setChartData(data);
  }, [candles]);
  
  // 当用户点击图表上的K线
  const handleChartClick = (event: any) => {
    if (!event || event.activeTooltipIndex === undefined) return;
    
    const index = event.activeTooltipIndex;
    if (typeof index !== 'number') return;
    
    // console.log("点击了K线:", index, chartData[index]);
    
    setShowInstructions(false);
    setSelectedIndex(index);
    
    // 直接创建分型
    createPatternFromSinglePoint(index);
  };
  
  // 自动识别顶底分型
  const autoDetectPattern = () => {
    setLoading(true);
    
    // 简单延迟以显示加载效果
    setTimeout(() => {
      try {
        if (selectedType === 'bottom') {
          // 寻找最低点
          let lowestIndex = 0;
          let lowestValue = candles[0].low;
          
          // 查找低点（避开最近的几根K线）
          for (let i = 1; i < candles.length - 5; i++) {
            if (candles[i].low < lowestValue) {
              lowestValue = candles[i].low;
              lowestIndex = i;
            }
          }
          
          // console.log("找到最低点:", lowestIndex, lowestValue);
          
          // 检查是否形成针形结构（前后K线的低点都高于该K线）
          if (lowestIndex > 0 && lowestIndex < candles.length - 1) {
            if (candles[lowestIndex - 1].low > lowestValue && 
                candles[lowestIndex + 1].low > lowestValue) {
              // 找到底分型
              setSelectedIndex(lowestIndex);
              
              // 直接创建底分型（使用当前K线的高低点）
              const selectedCandle = candles[lowestIndex];
              createBottomPattern(lowestIndex, selectedCandle);
              
              message.success(t('chart.selectedMessage', { index: lowestIndex + 1, mode: t('chart.mode.bottom') }));
            } else {
              message.warning(t('chart.autoDetect.noClearBottom' as TranslationKey));
              setSelectedIndex(lowestIndex);
              
              // 直接创建底分型（使用当前K线的高低点）
              const selectedCandle = candles[lowestIndex];
              createBottomPattern(lowestIndex, selectedCandle);
            }
          } else {
            message.warning(t('chart.autoDetect.noSuitableLow' as TranslationKey));
          }
        } else {
          // 寻找最高点
          let highestIndex = 0;
          let highestValue = candles[0].high;
          
          // 查找高点（避开最近的几根K线）
          for (let i = 1; i < candles.length - 5; i++) {
            if (candles[i].high > highestValue) {
              highestValue = candles[i].high;
              highestIndex = i;
            }
          }
          
          // console.log("找到最高点:", highestIndex, highestValue);
          
          // 检查是否形成针形结构（前后K线的高点都低于该K线）
          if (highestIndex > 0 && highestIndex < candles.length - 1) {
            if (candles[highestIndex - 1].high < highestValue && 
                candles[highestIndex + 1].high < highestValue) {
              // 找到顶分型
              setSelectedIndex(highestIndex);
              
              // 直接创建顶分型（使用当前K线的高低点）
              const selectedCandle = candles[highestIndex];
              createTopPattern(highestIndex, selectedCandle);
              
              message.success(t('chart.selectedMessage', { index: highestIndex + 1, mode: t('chart.mode.top') }));
            } else {
              message.warning(t('chart.autoDetect.noClearTop' as TranslationKey));
              setSelectedIndex(highestIndex);
              
              // 直接创建顶分型（使用当前K线的高低点）
              const selectedCandle = candles[highestIndex];
              createTopPattern(highestIndex, selectedCandle);
            }
          } else {
            message.warning(t('chart.autoDetect.noSuitableHigh' as TranslationKey));
          }
        }
      } catch (error) {
        console.error("自动识别失败:", error);
        message.error(t('chart.autoDetect.failed' as TranslationKey));
      } finally {
        setLoading(false);
      }
    }, 500);
  };
  
  // 创建底分型（提取为独立函数）
  const createBottomPattern = (index: number, candle: Candle) => {
    // 底分型: A是最低点，B是最高点
    const aPoint = candle.low;  // A点是当前K线最低点
    const bPoint = candle.high; // B点是当前K线最高点
    
    // 设置选中的时间和参考点值
    setSelectedTime(moment(candle.timestamp).format('YYYY-MM-DD HH:mm'));
    setReferencePointA(aPoint.toFixed(4));
    setReferencePointB(bPoint.toFixed(4));
    
    // console.log("底分型: A点(最低点)=", aPoint, "B点(最高点)=", bPoint, "差值=", bPoint - aPoint);
    
    // 计算预测线
    const predictionLines = calculatePredictionLines('bottom', aPoint, bPoint);
    
    // 检查预测线是否已达到
    const currentPrice = candles[candles.length - 1].close;
    const linesWithReachedStatus = predictionLines.map(line => {
      return { ...line, reached: currentPrice >= line.value };
    });
    
    // 创建新的分型
    const newPattern: PatternPoint = {
      type: 'bottom',
      xIndex: index,
      yIndex: index,
      zIndex: null,
      xCandle: candle,
      yCandle: candle,
      zCandle: null,
      highest: bPoint,
      lowest: aPoint,
      predictionLines: linesWithReachedStatus
    };
    
    setManualPattern(newPattern);
    
    // 更新图表数据，添加预测线
    updateChartWithPredictionLines(newPattern);
  };
  
  // 创建顶分型（提取为独立函数）
  const createTopPattern = (index: number, candle: Candle) => {
    // 顶分型: A是最高点，B是最低点
    const aPoint = candle.high; // A点是当前K线最高点
    const bPoint = candle.low;  // B点是当前K线最低点
    
    // 设置选中的时间和参考点值
    setSelectedTime(moment(candle.timestamp).format('YYYY-MM-DD HH:mm'));
    setReferencePointA(aPoint.toFixed(4));
    setReferencePointB(bPoint.toFixed(4));
    
    // console.log("顶分型: A点(最高点)=", aPoint, "B点(最低点)=", bPoint, "差值=", aPoint - bPoint);
    
    // 计算预测线
    const predictionLines = calculatePredictionLines('top', aPoint, bPoint);
    
    // 检查预测线是否已达到
    const currentPrice = candles[candles.length - 1].close;
    const linesWithReachedStatus = predictionLines.map(line => {
      return { ...line, reached: currentPrice <= line.value };
    });
    
    // 创建新的分型
    const newPattern: PatternPoint = {
      type: 'top',
      xIndex: index,
      yIndex: index,
      zIndex: null,
      xCandle: candle,
      yCandle: candle,
      zCandle: null,
      highest: aPoint,
      lowest: bPoint,
      predictionLines: linesWithReachedStatus
    };
    
    setManualPattern(newPattern);
    
    // 更新图表数据，添加预测线
    updateChartWithPredictionLines(newPattern);
  };
  
  // 根据单个点创建分型（手动选择时调用）
  const createPatternFromSinglePoint = (index: number) => {
    if (index >= candles.length) return;
    
    const selectedCandle = candles[index];
    
    if (selectedType === 'bottom') {
      // 创建底分型
      createBottomPattern(index, selectedCandle);
    } else {
      // 创建顶分型
      createTopPattern(index, selectedCandle);
    }
    
    message.success(t('chart.selectedMessage', { index: index + 1, mode: modeLabel }));
  };
  
  // 更新图表数据，添加预测线
  const updateChartWithPredictionLines = (pattern: PatternPoint) => {
    if (!pattern) return;
    
    const newChartData = [...chartData];
    
    // 添加预测线
    pattern.predictionLines.forEach(line => {
      const lineName = `线${line.lineNumber}`;
      
      // 从当前点开始，预测线添加到所有后续K线
      for (let i = pattern.xIndex; i < newChartData.length; i++) {
        newChartData[i][lineName] = line.value;
      }
    });
    
    setChartData(newChartData);
  };
  
  // 重置选择
  const handleReset = () => {
    setSelectedIndex(null);
    setManualPattern(null);
    setSelectedTime('');
    setReferencePointA('');
    setReferencePointB('');
    
    // 重置图表数据（移除预测线）
    if (candles.length > 0) {
      const resetData: ChartData[] = candles.map((candle, index) => {
        const isRise = candle.close >= candle.open;
        return {
          time: moment(candle.timestamp).format('MM-DD HH:mm'),
          open: candle.open,
          close: candle.close,
          high: candle.high,
          low: candle.low,
          volume: candle.volume,
          color: isRise ? '#26a69a' : '#ef5350',
          isRise,
          value: candle.close
        };
      });
      
      setChartData(resetData);
    }
    
    setShowInstructions(true);
  };
  
  // 切换顶底分型类型
  const handleTypeChange = (e: any) => {
    setSelectedType(e.target.value);
    
    // 如果已有选择，使用新类型重新计算
    if (selectedIndex !== null) {
      createPatternFromSinglePoint(selectedIndex);
    }
  };
  
  // 图表配置
  const getLineColor = (lineNumber: number) => {
    const colors = [
      '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe',
      '#00C49F', '#FFBB28', '#FF8042', '#a4de6c'
    ];
    return colors[(lineNumber - 1) % colors.length];
  };
  
  // 自定义K线提示
  const renderTooltip = (props: any) => {
    if (!props.active || !props.payload || !props.payload.length) return null;
    
    const data = props.payload[0].payload;
    
    // 创建自定义的HTML内容，完全控制显示
    return (
      <div 
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.96)',
          padding: '10px 12px',
          border: '1px solid rgba(229, 231, 235, 0.9)',
          borderRadius: '10px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
          minWidth: 220
        }}
      >
        {/* 时间行 */}
        <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '6px' }}>
          <p style={{ margin: 0, color: '#111827', fontWeight: 700 }}>
            {t('tooltip.time')}: {data.time}
          </p>
        </div>
        
        {/* 价格信息部分 */}
        <div style={{ margin: '8px 0 6px' }}>
          <p style={{ margin: '6px 0', color: '#374151' }}>
            {t('tooltip.open')}: <span style={{ color: '#111827', fontWeight: 600 }}>{data.open.toFixed(2)}</span>
          </p>
          <p style={{ margin: '6px 0', color: '#374151' }}>
            {t('tooltip.high')}: <span style={{ color: '#059669', fontWeight: 600 }}>{data.high.toFixed(2)}</span>
          </p>
          <p style={{ margin: '6px 0', color: '#374151' }}>
            {t('tooltip.low')}: <span style={{ color: '#dc2626', fontWeight: 600 }}>{data.low.toFixed(2)}</span>
          </p>
          <p style={{ margin: '6px 0', color: '#374151' }}>
            {t('tooltip.close')}: <span style={{ color: '#111827', fontWeight: 600 }}>{data.close.toFixed(2)}</span>
          </p>
        </div>
        
        {/* K线值部分 - 只显示一次 */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '6px', color: '#6b7280', fontSize: 12 }}>
          OKX
        </div>
      </div>
    );
  };
  
  // 自定义K线形状
  const CandlestickShape = (props: any): React.ReactElement => {
    const { x, width, index, datum, yScale } = props;
    
    // 如果没有数据或索引无效，返回空g元素而不是null
    if (!datum || index < 0 || index >= chartData.length) {
      return <g />;
    }
    
    const data = datum;
    const isRise = data.close >= data.open;
    const isSelected = index === selectedIndex;
    
    // 计算K线宽度
    const barWidth = Math.max(width * 0.7, 3); // 设置K线宽度，最小3px
    const barX = x - barWidth / 2 + width / 2; // 居中调整
    const centerX = x + width / 2; // K线中心点X坐标
    
    // 计算蜡烛实体位置（使用传入的yScale函数）
    const openY = yScale(data.open);
    const closeY = yScale(data.close);
    const highY = yScale(data.high);
    const lowY = yScale(data.low);
    
    // 确定实体的顶部和高度
    const barTop = Math.min(openY, closeY);
    const barHeight = Math.max(Math.abs(closeY - openY), 1);
    
    // 确定颜色
    const color = isRise ? '#26a69a' : '#ef5350';
    const strokeColor = isSelected ? '#FFD700' : color;
    const fillColor = isRise ? 'rgba(38, 166, 154, 0.8)' : 'rgba(239, 83, 80, 0.8)';
    const strokeWidth = isSelected ? 2 : 1;
    
    return (
      <g>
        {/* 上下影线 */}
        <line
          x1={centerX}
          y1={highY}
          x2={centerX}
          y2={barTop}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        <line
          x1={centerX}
          y1={barTop + barHeight}
          x2={centerX}
          y2={lowY}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        
        {/* 实体 */}
        <rect
          x={barX}
          y={barTop}
          width={barWidth}
          height={barHeight}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        
        {/* 选中标记 */}
        {isSelected && (
          <circle
            cx={centerX}
            cy={lowY}
            r={5}
            fill="#FFD700"
            stroke="#FF8C00"
            strokeWidth={1}
          />
        )}
      </g>
    );
  };
  
  return (
    <div className="chart-container">
      <Row gutter={isMobile ? [10, 10] : [16, 16]}>
        <Col span={24}>
          <Card className="chart-card">
            <Title level={4}>{t('chart.title', { mode: modeLabel })}</Title>
            
            <Space style={{ marginBottom: 16 }}>
              <Radio.Group 
                value={selectedType} 
                onChange={handleTypeChange} 
                buttonStyle="solid"
              >
                <Radio.Button value="bottom">{t('chart.mode.bottom')}</Radio.Button>
                <Radio.Button value="top">{t('chart.mode.top')}</Radio.Button>
              </Radio.Group>
              
              <Button 
                type="primary"
                onClick={autoDetectPattern}
                loading={loading}
              >
                {t('chart.autoDetect')}
              </Button>
              
              <Button 
                onClick={handleReset} 
                disabled={selectedIndex === null}
              >
                {t('chart.reset')}
              </Button>
            </Space>
            
            {showInstructions && (
              <Alert
                message={t('chart.instructionsTitle')}
                description={
                  <ul>
                    <li>{t('chart.instructions.1')}</li>
                    <li>{t('chart.instructions.2')}</li>
                    <li>{t('chart.instructions.3')}</li>
                    <li>{t('chart.instructions.4')}</li>
                  </ul>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            
            {chartData.length > 0 ? (
              <div style={{ height: isMobile ? 360 : 500, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart 
                    data={chartData}
                    onClick={handleChartClick}
                    margin={isMobile ? { top: 14, right: 22, left: 18, bottom: 14 } : { top: 20, right: 50, left: 100, bottom: 20 }}
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="time" 
                      scale="band"
                      interval={Math.ceil(chartData.length / (isMobile ? 10 : 15))} // 动态调整显示的刻度数量
                      tickFormatter={(value) => value.substring(0, 5)} // 只显示月日
                      stroke="rgba(102, 255, 102, 0.85)"
                      tick={{ fill: 'rgba(102, 255, 102, 0.85)' }}
                    />
                    <YAxis 
                      domain={yDomain ? yDomain : ['auto', 'auto']}
                      tickFormatter={(value) => value.toFixed(2)}
                      tickCount={8}
                      scale="linear"
                      orientation="right" // 在右侧显示Y轴刻度
                      label={{ value: t('table.price'), angle: 90, position: 'insideRight', fill: 'rgba(102, 255, 102, 0.85)' }}
                      stroke="rgba(102, 255, 102, 0.85)"
                      tick={{ fill: 'rgba(102, 255, 102, 0.85)' }}
                    />
                    <Tooltip content={renderTooltip} />
                    <Legend verticalAlign="top" wrapperStyle={{ color: 'rgba(102, 255, 102, 0.85)' }} />
                    
                    {/* 使用Bar组件绘制K线 */}
          <Bar
            dataKey="high"
                      name="K线"
                      shape={(props: any): React.ReactElement => {
                        const { x, y, width, height, index } = props;
                        // 始终返回一个React元素，不返回null
                        if (index === undefined || !chartData[index]) {
                          // 返回一个空的g元素，而不是null
                          return <g />;
                        }
                        
                        return <CandlestickShape 
                          index={index} 
                          datum={chartData[index]} 
                          x={x} 
                          y={y} 
                          width={width} 
                          height={height}
                          yScale={(v: number) => {
                            if (!yDomain) return 0;
                            const [min, max] = yDomain;
                            const range = max - min;
                            const ratio = (v - min) / range;
                            return props.y + props.height - (ratio * props.height);
                          }} 
                        />;
                      }}
                    />
                    
                    {/* 选中标记 */}
                    {selectedIndex !== null && (
                      <ReferenceLine
                        x={chartData[selectedIndex]?.time}
                        stroke="#ffc107"
                        strokeDasharray="3 3"
                        label={{ 
                          value: t('chart.selectedPoint', { mode: modeLabel }),
                          position: 'insideTopRight',
                          fill: '#ffc107'
                        }}
                      />
                    )}
          
          {/* 预测线 */}
                    {manualPattern && manualPattern.predictionLines.map(line => {
                      // 确定线的标签文字
                      let lineLabel = '';
                      if ([3, 6, 9].includes(line.lineNumber)) {
                        // 拐点的显示方式：3线→拐点1，6线→拐点2，9线→拐点3
                        const pivotNumber = [3, 6, 9].indexOf(line.lineNumber) + 1;
                        lineLabel = `${t('table.type.pivot')}${pivotNumber}`;
                      } else {
                        // 加仓点的显示方式：将1,2,4,5,7,8线分别映射为加仓点1-6
                        const supportPoints = [1, 2, 4, 5, 7, 8];
                        const supportNumber = supportPoints.indexOf(line.lineNumber) + 1;
                        lineLabel = `${t('table.type.target')}${supportNumber}`;
                      }
                      
                      return (
                        <ReferenceLine
                          key={`line-${line.lineNumber}`}
                          y={line.value}
                          stroke={getLineColor(line.lineNumber)}
                          strokeWidth={2}
                          label={{ 
                            value: lineLabel, 
                            position: 'insideLeft',
                            fill: getLineColor(line.lineNumber),
                            fontSize: 12,
                            offset: 10
                          }}
                        />
                      );
                    })}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Alert
                message={t('chart.loadingTitle')}
                description={t('chart.loadingDesc')}
                type="info"
                showIcon
              />
            )}
          </Card>
        </Col>
        
        {/* 添加趋势总结组件 */}
        {manualPattern && (
          <Col span={24}>
            <TrendSummary pattern={manualPattern} />
          </Col>
        )}
        
        {/* 原有的预测线信息Card */}
        {manualPattern && (
          <Col span={24}>
            <Card>
              <Title level={4}>
                {t('table.title', { mode: manualPattern.type === 'top' ? t('chart.mode.top') : t('chart.mode.bottom') })}
              </Title>
              <Divider />
              
              <Row gutter={isMobile ? [10, 10] : [16, 16]}>
                <Col span={24}>
                  <div className="reference-info">
                    <div className="reference-point">
                      <div className="reference-point-label">{t('chart.selectedTime' as TranslationKey)}：</div>
                      <div className="reference-point-value">{selectedTime}</div>
                    </div>
                    <div className="reference-point">
                      <div className="reference-point-label">{t('chart.entryRange' as TranslationKey)}：</div>
                      <div className="reference-point-value">{referencePointA} - {referencePointB}</div>
                    </div>
                  </div>
                </Col>
                
                <Col span={24}>
                  <table className="prediction-lines-table">
                    <thead>
                      <tr>
                        <th>{t('table.line')}</th>
                        <th>{t('table.price')}</th>
                        <th>{t('table.type')}</th>
                        <th>{t('table.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualPattern.predictionLines.map(line => {
                        // 确定线的类型和显示名称
                        let lineType = '';
                        let displayName = '';
                        
                        if ([3, 6, 9].includes(line.lineNumber)) {
                          // 拐点的显示方式
                          const pivotNumber = [3, 6, 9].indexOf(line.lineNumber) + 1;
                          lineType = t('table.type.pivot');
                          displayName = `${t('table.type.pivot')}${pivotNumber}`;
                        } else {
                          // 加仓点的显示方式
                          const supportPoints = [1, 2, 4, 5, 7, 8];
                          const supportNumber = supportPoints.indexOf(line.lineNumber) + 1;
                          lineType = t('table.type.target');
                          displayName = `${t('table.type.target')}${supportNumber}`;
                        }
                        
                        return (
                          <tr key={`table-line-${line.lineNumber}`} className={line.reached ? 'reached' : ''}>
                            <td>{displayName}</td>
                            <td>{line.value.toFixed(4)}</td>
                            <td>{lineType}</td>
                            <td>{line.reached ? t('table.status.reached') : t('table.status.notReached')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Col>
                
                <Col span={24}>
                  <Alert
                    message={t('tip.title')}
                    description={
                      manualPattern.type === 'top'
                        ? t('tip.desc.top')
                        : t('tip.desc.bottom')
                    }
                    type="info"
                    showIcon
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CandleStickChart;